import * as React from 'react';
import { createPortal } from 'react-dom';

import { cn } from '~/lib/utils';

interface DropdownMenuProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface DropdownMenuContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerRect: DOMRect | null;
  contentRef: React.RefObject<HTMLDivElement | null>;
}

const DropdownMenuContext =
  React.createContext<DropdownMenuContextValue | null>(null);

type ClickableElement = React.ReactElement<{
  onClick?: React.MouseEventHandler<HTMLElement>;
}>;

function useDropdownMenu() {
  const ctx = React.useContext(DropdownMenuContext);
  if (!ctx)
    throw new Error(
      'DropdownMenu compound components must be used within <DropdownMenu>'
    );
  return ctx;
}

export function DropdownMenu({
  children,
  open: controlledOpen,
  onOpenChange
}: DropdownMenuProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [triggerRect, setTriggerRect] = React.useState<DOMRect | null>(null);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const insideTrigger = wrapperRef.current?.contains(target) ?? false;
      const insideContent = contentRef.current?.contains(target) ?? false;
      if (!insideTrigger && !insideContent) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open, setOpen]);

  React.useEffect(() => {
    if (!open) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, setOpen]);

  React.useLayoutEffect(() => {
    if (open && wrapperRef.current) {
      setTriggerRect(wrapperRef.current.getBoundingClientRect());
    }
  }, [open]);

  return (
    <DropdownMenuContext.Provider
      value={{ open, onOpenChange: setOpen, triggerRect, contentRef }}
    >
      <div ref={wrapperRef} className="relative w-fit shrink-0 text-left">
        {children}
      </div>
    </DropdownMenuContext.Provider>
  );
}

export function DropdownMenuTrigger({
  children,
  asChild
}: {
  children: React.ReactNode;
  asChild?: boolean;
}) {
  const { onOpenChange, open } = useDropdownMenu();
  if (asChild && React.isValidElement(children)) {
    const child = children as ClickableElement;
    return React.cloneElement(child, {
      onClick: (e: React.MouseEvent) => {
        child.props.onClick?.(e as React.MouseEvent<HTMLElement>);
        if (e.defaultPrevented) return;
        e.stopPropagation();
        onOpenChange(!open);
      }
    });
  }
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onOpenChange(!open);
      }}
    >
      {children}
    </button>
  );
}

export function DropdownMenuContent({
  children,
  className,
  align = 'center',
  sideOffset = 4
}: {
  children: React.ReactNode;
  className?: string;
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
}) {
  const { open, triggerRect, contentRef } = useDropdownMenu();
  const [measuredHeight, setMeasuredHeight] = React.useState(0);

  React.useLayoutEffect(() => {
    if (open && contentRef.current) {
      setMeasuredHeight(contentRef.current.getBoundingClientRect().height);
    }
  }, [open, children, contentRef]);

  if (!open || !triggerRect || typeof document === 'undefined') return null;

  const spaceBelow = window.innerHeight - triggerRect.bottom - sideOffset;
  const shouldFlip = measuredHeight > 0 && measuredHeight > spaceBelow;
  const top = shouldFlip
    ? triggerRect.top - measuredHeight - sideOffset
    : triggerRect.bottom + sideOffset;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 50
      }}
    >
      <div
        style={{
          position: 'absolute',
          top,
          left: align === 'start' ? triggerRect.left : undefined,
          right:
            align === 'end' ? window.innerWidth - triggerRect.right : undefined,
          transform: align === 'center' ? 'translateX(-50%)' : undefined,
          marginLeft:
            align === 'center'
              ? triggerRect.left + triggerRect.width / 2
              : undefined,
          pointerEvents: 'auto'
        }}
      >
        <div
          ref={contentRef}
          className={cn(
            'min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95',
            className
          )}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}

export function DropdownMenuItem({
  children,
  className,
  onClick,
  disabled
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: (() => void) | (() => Promise<void>);
  disabled?: boolean;
}) {
  const { onOpenChange } = useDropdownMenu();
  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        'relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground disabled:pointer-events-none disabled:opacity-50',
        className
      )}
      onClick={() => {
        const result = onClick?.();
        if (result && typeof result.then === 'function') {
          result.catch(() => {
            // Ignore async errors here; callers should handle their own errors.
          });
        }
        onOpenChange(false);
      }}
    >
      {children}
    </button>
  );
}

export function DropdownMenuSeparator({ className }: { className?: string }) {
  return <div className={cn('-mx-1 my-1 h-px bg-muted', className)} />;
}

export function DropdownMenuLabel({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('px-2 py-1.5 text-sm font-semibold', className)}>
      {children}
    </div>
  );
}
